import { Card } from "@rothoblaas/design-system";
import { memo, type FC } from "react";
import useI18n from "../../../hooks/useTranslations";
import type { DeckDesignerResult } from "../../../http/deckDesigner/formulas";
import styles from "./CalculationDetailsCard.module.css";
import { useMeasurementFormat } from "./measurementFormat";
import { EMPTY_VALUE, formatQuantity, isFiniteNumber } from "../../../utils/numberFormat";

type CalculationDetailsCardProps = {
  result: DeckDesignerResult | null;
  hasAnyResult: boolean;
  // Renders just the title and content, as a section of a parent Card, instead of its own Card.
  embedded?: boolean;
};

const Row: FC<{ label: string; value: string }> = ({ label, value }) => (
  <tr>
    <td>{label}</td>
    <td className={styles.value}>{value}</td>
  </tr>
);

const Section: FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <h3 className={styles.sectionTitle}>{title}</h3>
    {children}
  </div>
);

const CalculationDetailsCard: FC<CalculationDetailsCardProps> = ({ result, hasAnyResult, embedded = false }) => {
  const i18n = useI18n();
  const languageCode = i18n.translation.languageCode;
  const common = i18n.translation.common;
  const measurementFormat = useMeasurementFormat();
  const calculation = i18n.translation.deckDesignerModule.configurations.calculation;

  const fmtQuantity = (value: number | null | undefined, unit: string, decimals = 0): string =>
    formatQuantity(languageCode, value, unit, decimals);

  const content =
    hasAnyResult && result ? (
      <section className={styles.calculationDetailsSection}>
        <Section title={calculation.layoutCalculation}>
          <table className={styles.table}>
            <tbody>
              <Row
                label={calculation.deckArea}
                value={
                  isFiniteNumber(result.deckArea)
                    ? measurementFormat.area(result.deckArea, fmtQuantity(result.deckArea, common.unitM2, 2))
                    : EMPTY_VALUE
                }
              />
              <Row
                label={calculation.regularLongBoards}
                value={fmtQuantity(result.boardQuantity?.regularLong, common.unitPcs)}
              />
              <Row
                label={calculation.regularShortBoards}
                value={fmtQuantity(result.boardQuantity?.regularShort, common.unitPcs)}
              />
              <Row
                label={calculation.differentLongBoards}
                value={fmtQuantity(result.boardQuantity?.differentLong, common.unitPcs)}
              />
              <Row
                label={calculation.differentShortBoards}
                value={fmtQuantity(result.boardQuantity?.differentShort, common.unitPcs)}
              />
              <Row label={calculation.totalBoards} value={fmtQuantity(result.boardQuantity?.total, common.unitPcs)} />
            </tbody>
          </table>
        </Section>

        <Section title={i18n.translation.deckDesignerModule.substructures.singular}>
          <table className={styles.table}>
            <tbody>
              <Row label={calculation.joists} value={fmtQuantity(result.substructureQuantity?.total, common.unitPcs)} />
              <Row
                label={calculation.longJoists}
                value={fmtQuantity(result.substructureQuantity?.long, common.unitPcs)}
              />
              <Row
                label={calculation.shortJoists}
                value={fmtQuantity(result.substructureQuantity?.short, common.unitPcs)}
              />
            </tbody>
          </table>
        </Section>

        <Section title={calculation.supportCalculation}>
          <table className={styles.table}>
            <tbody>
              <Row
                label={calculation.maxSupportSpacing}
                value={
                  isFiniteNumber(result.supportQuantity?.maxSpacingCm)
                    ? measurementFormat.length(
                        result.supportQuantity.maxSpacingCm,
                        "cm",
                        fmtQuantity(result.supportQuantity.maxSpacingCm, common.unitCm),
                      )
                    : EMPTY_VALUE
                }
              />
              <Row
                label={calculation.supportsPerLongJoist}
                value={fmtQuantity(result.supportQuantity?.long, common.unitPcs)}
              />
              <Row
                label={calculation.supportsPerShortJoist}
                value={fmtQuantity(result.supportQuantity?.short, common.unitPcs)}
              />
            </tbody>
          </table>
        </Section>
      </section>
    ) : (
      <div className={styles.empty}>{calculation.calculationDetailsEmpty}</div>
    );

  // Embedded in the results Card's tabs: the tab title already names it.
  return embedded ? (
    content
  ) : (
    <Card
      style={{ minHeight: 400 }}
      accentColor="var(--accent-base, var(--red))"
      shadow
      title={calculation.calculationDetails}
    >
      {content}
    </Card>
  );
};

// Memoized: DeckDesignerResults re-renders on every result change, but this component's props are
// stable - it re-renders through its own watched fields and props only.
export default memo(CalculationDetailsCard);
